import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Box, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useQueryClient } from "react-query";
import {
  capitalGoodsKeys,
  organizationQuestionnaireKeys,
  useAddCapitalGoodsItems,
  useAttachFileToCategory, useDeleteCategoryAttachment,
  useDeleteCapitalGoodsItem, useGetCategoryAttachment,
  useGetCapitalGoodsItems,
  useGetLocations,
  useUpdateCapitalGoods,
  useUpdateCapitalGoodsItem
} from "../../../../../../hooks/query";
import { FormTextArea } from "components/Forms";
import CategoryForm from "./CategoryForm";
import {
  showErrorToast,
  showSuccessToast
} from "../../../../../../utils";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { setAction } from "../../../../../../context/Store/slices/questionnaireSlice";
import { useDispatch } from "react-redux";
import { ManualInput } from "../ManualInput";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { CategoryGrid } from "../CategoryGrid";
import { DeleteDialog } from "../../../../../DataEntryPlanning/components/DeleteDialog";
import { PurchasedAndCapitalGoodsModalWrapper } from "../CategoryModalWrappers";
import { FileAttachment } from "../../../FileAttachment";

const FORM_INPUT_KEY = "capitalGoods";

function CapitalGoods({
                        defaultValues,
                        tabKey,
                        handleCategoryChange,
                        finalize,
                        finalizeQuestionnaire,
                        setFinalize,
                        declineReturnToListRequest,
                        isPreview
                      }) {
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });

  const {
    data: locations
  } = useGetLocations({
    onError: err => handleErrors(err)
  });

  const {
    id: questionnaireId,
    categories: {
      capitalGoods: {
        id: categoryId,
        items,
        notes,
        manualInputOfEmissions,
        totalCo2Emissions,
        attachment
      } = {
        id: null,
        items: [],
        notes: "",
        totalCo2Emissions: null,
        manualInputOfEmissions: false,
        attachment: null
      }
    }
  } = defaultValues;

  const colHeaders = [
    t(`inputs.${tabKey}.supplierName`),
    t(`inputs.${tabKey}.location`),
    t(`inputs.${tabKey}.productCode`),
    t(`inputs.${tabKey}.itemDescription`),
    t(`inputs.${tabKey}.spentAmount`)
  ];
  const attributesOrder = ["supplierName", "location", "productCategory", "itemDescription", "spentMoneyAmount"];

  const [categoryModalData, setCategoryModalData] = useState(null);

  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const handleErrors = (err) => {
    if (err.response?.status === 404) {
      return;
    }
    showErrorToast(toast, err.message);
  };

  const [isDirty, setIsDirty] = useState(false);

  const capitalGoodsDefaultValues = {
    notes,
    manualInputOfEmissions,
    totalCo2Emissions
  };

  const {
    register,
    control,
    handleSubmit,
    formState,
    watch,
    setValue
  } = useForm({
    defaultValues: capitalGoodsDefaultValues
  });

  const prepareSectionValues = useCallback(items => {
    return items.map(item => {
      return {
        id: item.id ?? null,
        location: locations?.find(el => el.id === item.locationId)?.location ?? "",
        locationId: item.locationId,
        productCategory: `${item.productCode} - ${item.productCategory}`,
        productCategoryId: item.productCategoryId ?? null,
        supplierName: item.supplierName,
        itemDescription: item.itemDescription,
        spentMoneyAmount: item.spentMoneyAmount
      };
    });
  }, [locations]);

  const handleAddItem = () => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: t(`inputs.capitalGoods.itemsType`)
    });
    onCategoryModalOpen();
  };

  const handleEditItem = (item) => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        productCategory: item.productCategory,
        productCategoryId: item.productCategoryId,
        supplierName: item.supplierName,
        itemDescription: item.itemDescription,
        spentMoneyAmount: item.spentMoneyAmount,
        locationId: item.locationId,
        location: item.location
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: t(`inputs.capitalGoods.itemsType`)
    });
    onCategoryModalOpen();
  };

  const [selectedItem, setSelectedItem] = useState(null);

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id });
  };

  useEffect(() => {
    if (formState.errors && Object.keys(formState.errors).length > 0) {
      // If there is a validation error we decline all previously requested actions like switching to different category,
      // finalisation of the questionnaire or 'Return to list'
      dispatch(setAction(""));
      if (finalize) {
        setFinalize(false);
      }
      if (declineReturnToListRequest) {
        declineReturnToListRequest();
      }
    }
    if (formState.isDirty !== isDirty) {
      // Workaround that guarantees the actual value of isDirty attribute is used
      setIsDirty(formState.isDirty);
    }
  }, [formState, isDirty, declineReturnToListRequest, finalize, setFinalize, dispatch]);

  const queryConfig = {
    onSuccess: () => {
      showSuccessToast(toast, "Category Capital Goods saved!");
      if (finalize) {
        finalizeQuestionnaire(questionnaireId);
      } else {
        queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
        if (!isLoadingOnUpdate) {
          handleCategoryChange();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message)
  };

  const formId = `formStep${tabKey}`;
  const { mutate: update, isLoading: isLoadingOnUpdate } = useUpdateCapitalGoods(queryConfig);

  const { mutate: deleteItem } = useDeleteCapitalGoodsItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(capitalGoodsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const onSubmit = ((data) => {
    if (!isPreview) {
      if (isDirty) {
        const requestData = {
          items,
          notes: data.notes,
          totalCo2Emissions: data.totalCo2Emissions,
          manualInputOfEmissions: data.manualInputOfEmissions
        };
        update({ questionnaireId, data: requestData });
      } else {
        finalize ? finalizeQuestionnaire(questionnaireId) : handleCategoryChange();
      }
    } else {
      handleCategoryChange();
    }
  });

  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const { mutate: addItem } = useAddCapitalGoodsItems({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(capitalGoodsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdateCapitalGoodsItem({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(capitalGoodsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleCategoryModalSubmit = (formData) => {
    const apiData = [];
    formData[categoryModalData.fieldArrayName].forEach(el => apiData.push(el));

    dispatch(showAppSpinner());
    if (categoryModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0]
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData
      });
    }
  };

  return (
    <CategoryForm onSubmit={handleSubmit(onSubmit)} id={formId} marginTop={4}>
      <Text opacity="0.75" fontSize="sm" marginBlockEnd="4">
        {t(`categories.${tabKey}Desc`)}
      </Text>

      {locations && <CategoryGrid
        questionnaireId={questionnaireId}
        columnHeaders={colHeaders}
        attributesOrder={attributesOrder}
        prepareSectionValues={prepareSectionValues}
        handleAddItem={handleAddItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        getItemsFunction={useGetCapitalGoodsItems}
        isPreview={isPreview}
        maxHeight={isPreview ? "240" : "210"}
      />}

      <ManualInput
        register={register}
        errors={formState.errors}
        control={control}
        watch={watch}
        setValue={setValue}
        isPreview={isPreview}
      />

      <FileAttachment
        type={"capital-goods"}
        uploadHook={useAttachFileToCategory}
        downloadHook={useGetCategoryAttachment}
        deleteHook={useDeleteCategoryAttachment}
        invalidationKey={organizationQuestionnaireKeys.orgQuestionnaireById()}
        currentAttachment={attachment}
        params={{
          questionnaireId: questionnaireId,
          categoryId: categoryId
        }}
        isDisabled={isPreview}
      />

      <Box paddingTop="4">
        <FormTextArea
          label={t("inputs.notes")}
          errors={formState.errors}
          {...register("notes")}
          disabled={isPreview}
          maxHeight="50"
        />
      </Box>

      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />

      {
        isCategoryModalOpen && categoryModalData !== null && (
          <PurchasedAndCapitalGoodsModalWrapper
            tabKey={tabKey}
            modalData={categoryModalData}
            attributesOrder={attributesOrder}
            isOpen={isCategoryModalOpen}
            onClose={handleCategoryModalClose}
            onSubmit={handleCategoryModalSubmit}
            locations={locations}
          />
        )
      }
    </CategoryForm>
  );
}

export default CapitalGoods;
